import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import store from '@/store';
import type { AnalysisStep } from '@/types/analytics/AnalysisStep';
import { PORTFOLIO_CONSTRUCTION } from '@/types/analytics/PortfolioConstruction';
import type { IItemUnderAnalysis } from '@/types/IItemUnderAnalysis';
import { PeriodModule } from './PeriodStore';
import { createDate } from '@/utils/dateUtils';
import type { IPortfolioTreeSubportfolio, IPortfolioTreeStrategy, IPortfolioTree } from '@/types/IPortfolioTree';
import { isPortfolioTreeStrategy } from '@/types/IPortfolioTree';
import { isConstituentPortfolioFn, isPortfolioYoung } from '@/utils/portfolioTree';
import type { StrategyFactsheetResponseDTO } from '@/api-v2/web/strategies';
import { isStrategyFactsheetResponseDTO } from '@/api-v2/web/strategies';
import useTranslation from '@/composables/useTranslation';
import useFxConversion from '@/composables/useFxConversion';
import useSystemLoading from '@/composables/useSystemLoading';
import { LoadingItems } from '@/constants/LoadingItems';
import { FxConversion } from '@/constants/FxConversion';
import { useToasts } from '@/composables/useToasts';
import type { PortfolioControllerConfig } from '@/types/PortfolioControllerConfig';
import type { RouteLocationNormalized } from 'vue-router';
import { useItemUnderAnalysisGlobalState } from '@/composables/useItemUnderAnalysis';
import { resolvePortfolioDisabledBeforeDate } from '@/utils/resolvePortfolioDisabledBeforeDate';

const getLiveDateForStrategy = (item: IPortfolioTreeStrategy | StrategyFactsheetResponseDTO): string | null => {
  if (!item.strategy) return null;

  return item.strategy.inceptionDate;
};

const getDisabledBeforeDateForStrategy = (
  item: IPortfolioTreeStrategy | StrategyFactsheetResponseDTO,
): string | null => {
  if (!item.strategy) return null;

  // We want to pick the nearest date
  return item.strategy.effectiveHistoryStartDate &&
    createDate(item.strategy.effectiveHistoryStartDate) > createDate(item.strategy.historyStartDate)
    ? item.strategy.effectiveHistoryStartDate
    : item.strategy.historyStartDate;
};

/**
 * Tree later-of start: later of historyStartDate and effectiveHistoryStartDate.
 * Prefer draft.availableDateRange.startDate when present (see resolvePortfolioDisabledBeforeDate).
 */
const getDisabledBeforeDateForSubportfolio = (subportfolio: IPortfolioTreeSubportfolio) => {
  let disabledBeforeDate = subportfolio.historyStartDate;

  // We want to pick the nearest date
  if (createDate(subportfolio.effectiveHistoryStartDate) > createDate(disabledBeforeDate)) {
    disabledBeforeDate = subportfolio.effectiveHistoryStartDate;
  }

  return disabledBeforeDate;
};

@Module({ dynamic: true, store, name: 'AnalyticsStore' })
class AnalyticsStore extends VuexModule {
  public activeAnalysisStep: AnalysisStep = PORTFOLIO_CONSTRUCTION;
  public isLegendModeActive = false;

  public navigationPanelConfig: PortfolioControllerConfig = {
    shouldColorImmediateChildren: true,
    shouldColorChildrenRecursively: false,
    canMakeStrategyActiveItem: false,
    onFirstRouteChange: (_route: RouteLocationNormalized) => {},
  };

  public allocationModalOpened = false;
  public allocationModalPortfolioId: string | null = null;

  public itemUnderAnalysis: IItemUnderAnalysis | null = null;

  @Mutation
  private SET_NAVIGATION_PANEL_CONFIG(newVal: PortfolioControllerConfig): void {
    this.navigationPanelConfig = newVal;
  }
  @Action({ rawError: true })
  public async SetNavigationPanelConfig(newVal: PortfolioControllerConfig): Promise<void> {
    this.SET_NAVIGATION_PANEL_CONFIG(newVal);
  }

  @Mutation
  private SET_ITEM_UNDER_ANALYSIS({ newVal }: { newVal: IItemUnderAnalysis | null }): void {
    this.itemUnderAnalysis = newVal;
  }

  @Action({ rawError: true })
  public async SetAvailablePeriodsForItemUnderAnalysis({
    newVal,
    draft,
    triggerWarningIfPeriodChanges,
    shouldSuppressErrors,
    disabledAfterDate,
  }: {
    newVal: IItemUnderAnalysis;
    draft?: IPortfolioTree | null;
    triggerWarningIfPeriodChanges: boolean;
    shouldSuppressErrors: boolean;
    disabledAfterDate?: string;
  }): Promise<void> {
    if (isStrategyFactsheetResponseDTO(newVal) || isPortfolioTreeStrategy(newVal)) {
      await this.SetAvailablePeriodsForStrategy({ newVal, triggerWarningIfPeriodChanges, disabledAfterDate });
      return;
    }

    if (!draft) {
      console.error('MISSING DRAFT...');
      return;
    }

    await this.SetAvailablePeriodsForPortfolio({
      subportfolio: newVal,
      draft,
      triggerWarningIfPeriodChanges,
      shouldSuppressErrors,
      disabledAfterDate,
    });
  }

  /**
   * setPeriod parameter should be removed once the Navigation Panel
   * has been integrated into the factsheets
   */
  @Action({ rawError: true })
  public async SetItemUnderAnalysis({
    newVal,
    draft,
    triggerWarningIfPeriodChanges = false,
    shouldSuppressErrors = false,
    setPeriod = true,
    showLoading = true,
  }: {
    newVal: IItemUnderAnalysis | null;
    draft?: IPortfolioTree | null;
    triggerWarningIfPeriodChanges?: boolean;
    shouldSuppressErrors?: boolean;
    setPeriod?: boolean;
    showLoading?: boolean;
  }): Promise<void> {
    const { addLoadingItem, removeLoadingItem } = useSystemLoading();
    const { fxType, toCurrency, changeFxConversionParam, setOriginalCurrency } = useFxConversion();
    const { itemUnderAnalysis, portfolioTreeUnderAnalysis, shouldExposePositions, containsOnlyEquities } =
      useItemUnderAnalysisGlobalState();

    if (setPeriod && newVal) {
      if (showLoading) addLoadingItem(LoadingItems.ITEM_UNDER_ANALYSIS_DATE);
      await this.SetAvailablePeriodsForItemUnderAnalysis({
        newVal,
        draft,
        triggerWarningIfPeriodChanges,
        shouldSuppressErrors,
      });
      if (showLoading) removeLoadingItem(LoadingItems.ITEM_UNDER_ANALYSIS_DATE);
    }

    this.SET_ITEM_UNDER_ANALYSIS({ newVal });
    itemUnderAnalysis.value = newVal;

    if (isStrategyFactsheetResponseDTO(newVal)) {
      portfolioTreeUnderAnalysis.value = null;
      toCurrency.value = newVal.strategy.currency;
      fxType.value = FxConversion.UNHEDGED;
      setOriginalCurrency(newVal.strategy.currency);

      shouldExposePositions.value = newVal.strategyApiConfig.shouldExposePositions ?? null;
      containsOnlyEquities.value = newVal.strategyApiConfig.containsOnlyEquities ?? null;
      return;
    }

    if (!draft) {
      portfolioTreeUnderAnalysis.value = null;
      shouldExposePositions.value = null;
      containsOnlyEquities.value = null;
      return;
    }

    portfolioTreeUnderAnalysis.value = draft;

    shouldExposePositions.value = draft.apiConfig?.shouldExposePositions ?? null;
    containsOnlyEquities.value = draft.apiConfig?.containsOnlyEquities ?? null;

    // For a constituent portfolio, ensure that the fx type is not converted
    if (isConstituentPortfolioFn(draft)) {
      draft.portfolioTree.fxType = FxConversion.NOT_CONVERTED;
    }

    const newToCurrency = draft.portfolioTree.toCurrency;
    const newFxType = draft.portfolioTree.fxType;

    // For portfolios that have their own portfolio currency, make sure that all children are converted to the new root currency and fx type
    changeFxConversionParam(draft.portfolioTree, {
      fxType: newFxType,
      toCurrency: newToCurrency,
    });

    if (newToCurrency) toCurrency.value = newToCurrency;

    if (newFxType) fxType.value = newFxType;
  }

  @Mutation
  public OPEN_ALLOCATION_MODAL(portfolioId: string): void {
    this.allocationModalPortfolioId = portfolioId;
    this.allocationModalOpened = true;
  }

  @Mutation
  public CLOSE_ALLOCATION_MODAL() {
    this.allocationModalOpened = false;
  }

  @Mutation
  private SET_IS_LEGEND_MODE_ACTIVE(newVal: boolean): void {
    this.isLegendModeActive = newVal;
  }
  @Action({ rawError: true })
  public async SetIsLegendModeActive(newVal: boolean): Promise<void> {
    this.SET_IS_LEGEND_MODE_ACTIVE(newVal);
  }

  @Mutation
  private SET_ACTIVE_ANALYSIS_STEP(name: AnalysisStep): void {
    this.activeAnalysisStep = name;
  }
  @Action({ rawError: true })
  public async SetActiveAnalysisStep(name: AnalysisStep): Promise<void> {
    this.SET_ACTIVE_ANALYSIS_STEP(name);
  }

  /**
   * This function does a few things:
   * 1. It sets the Live Date for the Portfolio. This happens BEFORE
   *    setting the disabledBeforeDate because if the disabledBeforeDate
   *    invalidates the currentPeriod (i.e., the disabledBeforeDate is
   *    after the current analysis fromDate) then a new period must be set.
   *    We must know what the Live Date is at that point so that we can choose it if applicable.
   * 2. Then it sets the disabledBeforeDate (which can also change the current
   *    period, as described in Step 1)
   * 3. Then it sets the disabledAfterDate
   */
  @Action({ rawError: true })
  public async SetAvailablePeriodsForPortfolio({
    subportfolio,
    draft,
    triggerWarningIfPeriodChanges,
    shouldSuppressErrors,
    disabledAfterDate,
  }: {
    subportfolio: IPortfolioTreeSubportfolio;
    draft: IPortfolioTree;
    triggerWarningIfPeriodChanges: boolean;
    shouldSuppressErrors: boolean;
    disabledAfterDate?: string;
  }): Promise<void> {
    const { errorToast } = useToasts();
    const { translate } = useTranslation();

    const isSuppressingErrors = shouldSuppressErrors || (draft && isPortfolioYoung(draft));

    if (subportfolio.flattenedActiveCodes && !subportfolio.flattenedActiveCodes.length && !isSuppressingErrors) {
      errorToast(translate({ path: 'ERROR.NO_ACTIVE_STRATEGIES' }));
    }

    if (subportfolio.inceptionDate) {
      PeriodModule.SetLiveDate(subportfolio.inceptionDate);
    }

    // The draft is safely passed in to this function for all portfolio cases, so this should never occur
    if (!draft) {
      console.error('MISSING DRAFT...');
      return;
    }

    const disabledBeforeDate = resolvePortfolioDisabledBeforeDate({
      availableDateRangeStartDate: draft.availableDateRange?.startDate,
      treeDisabledBeforeDate: getDisabledBeforeDateForSubportfolio(subportfolio),
      analysisEndDate: disabledAfterDate || PeriodModule.disabledAfterDate,
    });
    if (!disabledBeforeDate) {
      console.error(translate({ path: 'ERROR.DISABLED_BEFORE_DATE_UNDEFINED' }));
      return;
    }

    await PeriodModule.SetDisabledBeforeAfterDates({
      beforeDate: disabledBeforeDate,
      afterDate: disabledAfterDate,
      triggerWarning: triggerWarningIfPeriodChanges,
    });
  }

  @Action({ rawError: true })
  public async SetAvailablePeriodsForStrategy({
    newVal,
    triggerWarningIfPeriodChanges,
    disabledAfterDate,
  }: {
    newVal: IPortfolioTreeStrategy | StrategyFactsheetResponseDTO;
    triggerWarningIfPeriodChanges: boolean;
    disabledAfterDate?: string;
  }): Promise<void> {
    const { translate } = useTranslation();

    const liveDate = getLiveDateForStrategy(newVal);
    const disabledBeforeDate = getDisabledBeforeDateForStrategy(newVal);

    if (liveDate) {
      PeriodModule.SetLiveDate(liveDate);
    }

    if (!disabledBeforeDate) {
      console.error(translate({ path: 'ERROR.DISABLED_BEFORE_DATE_UNDEFINED' }));
      return;
    }

    await PeriodModule.SetDisabledBeforeAfterDates({
      beforeDate: disabledBeforeDate,
      afterDate: disabledAfterDate,
      triggerWarning: triggerWarningIfPeriodChanges,
    });
  }
}

export default getModule(AnalyticsStore);
